import { createInstance, registerGlobalPlugins } from '@module-federation/enhanced/runtime';
import type { DomainCode } from '@parrot-software/observability';
import React from 'react';

import { createMfeError, MFE_ERROR_NAMES } from './errors';
import { createSharedGuardPlugin } from './plugins';
import { isRemoteUrlAllowed, resolveRemoteUrl, RemoteConfig } from './remoteUrl';
import { hostSharedDependencies, TOOLKIT_SHARED_SPECIFIER_VERSIONS } from './sharedDependencies';

let instance: ReturnType<typeof createInstance> | null = null;
const registeredRemotes = new Map<string, DomainCode>();

const sharedGuardPlugin = createSharedGuardPlugin({
  hostVersions: {
    ...TOOLKIT_SHARED_SPECIFIER_VERSIONS,
    react: React.version,
    'react-dom': React.version,
    'react/jsx-runtime': React.version,
  },
  resolveDomain: (remoteName) => registeredRemotes.get(remoteName),
});

/**
 * Module Federation runtime host (pure runtime mode — no webpack build
 * changes). ONE instance for the whole shell, shared by every remote.
 * Singleton mirroring the domains' composition `init`/`get` pattern.
 */
export const getMfeHost = (): ReturnType<typeof createInstance> => {
  if (!instance) {
    registerGlobalPlugins([sharedGuardPlugin]);
    // loaded-first hands remotes the copy the host already loaded and initialized (the telemetry
    // SDK above all) instead of the highest version on the page.
    instance = createInstance({
      name: 'parrot_connect_host',
      plugins: [sharedGuardPlugin],
      remotes: [],
      shareStrategy: 'loaded-first',
      shared: hostSharedDependencies,
    });
  }

  return instance;
};

/**
 * Registers a remote on the host (idempotent). Remotes self-register lazily
 * on first load, so adding a new MFE never touches the host runtime code.
 */
export const registerRemote = (config: RemoteConfig): void => {
  if (registeredRemotes.has(config.name)) {
    return;
  }

  const baseUrl = resolveRemoteUrl(config);
  if (!baseUrl) {
    throw createMfeError(MFE_ERROR_NAMES.DISABLED, `Remote "${config.name}" is not enabled for this environment`);
  }
  if (!isRemoteUrlAllowed(baseUrl)) {
    throw createMfeError(MFE_ERROR_NAMES.NOT_ALLOWED, `Remote "${config.name}" origin is not in the allowlist`);
  }

  getMfeHost().registerRemotes([{ entry: `${baseUrl}/mf-manifest.json`, name: config.name }]);
  registeredRemotes.set(config.name, config.domain);
};
